import { useAppDispatch, useAppSelector } from "@/store"
import React, { useRef, useState, useEffect } from "react"
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Text,
} from "@chakra-ui/react"

import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons"
import { useLoginOld } from "./login_hook"
import {
  changeViewType,
  LoginApiVersion,
  LoginStatus,
  selectLoginState,
  selectStopResumeType,
  setLoginApiVersion,
  setShouldShowProcessing,
  updateLoginStatus,
  ViewType,
} from "@/stores/login_slice"

const StopResumePwdVerify: React.FC = () => {
  const stopResumeType = useAppSelector(selectStopResumeType)
  const pwd = useRef<string | null>(null)
  const [showViewIcon, setShowViewIcon] = useState(false)
  const dispatch = useAppDispatch()
  const loginOld = useLoginOld()
  const { currentUser, msg, shouldShowProcessing, status } =
    useAppSelector(selectLoginState)

  const setErrMsg = (msg: string) => {
    dispatch(updateLoginStatus({ msg, status: LoginStatus.error }))
  }

  // KS用户从手机验证返回时，操作已经执行完成或失败，这里只清理跳转标记。
  useEffect(() => {
    if (shouldShowProcessing) {
      dispatch(setShouldShowProcessing(false))
    }
  }, [dispatch, shouldShowProcessing])

  const handleConfirmPwd = async () => {
    const pwdStr = pwd.current
    if (pwdStr === null || pwdStr.length === 0) {
      setErrMsg("请输入云电脑登录密码")
      return
    }

    try {
      // 云电脑关机/重启的 密码校验
      await loginOld({ userName: currentUser?.account, pwd: pwdStr })

      // 操作进度和最终结果由 userLoginOld 写入 Redux 状态。
    } catch (error) {
      console.error("Password verification failed:", error)
      // 错误会通过redux状态自动显示
    }
  }

  // 如果正在处理中，强制显示处理消息
  const actionType = stopResumeType === "stop" ? "关机" : "重启"
  const displayMsg = msg

  const isProcessing = status === LoginStatus.logging
  const isCompleted = status === LoginStatus.success
  const shouldShowPasswordInput = !isProcessing && !isCompleted
  const isButtonDisabled = isProcessing
  const closeDialog = () => {
    dispatch(changeViewType(ViewType.login))
    dispatch(setLoginApiVersion(LoginApiVersion.newApi))
  }
  const confirmButtonText = isCompleted ? "确定" : actionType
  const isProgressMsg =
    displayMsg === "正在验证用户身份..." ||
    displayMsg === "云电脑正在重启，请稍候..." ||
    displayMsg === "云电脑正在关机，请稍候..."
  const isSuccessMsg = displayMsg === "重启成功" || displayMsg === "关机成功"

  return (
    <Flex
      width="100vw"
      height="100vh"
      position="fixed"
      left={0}
      top={0}
      backgroundColor="rgba(0, 0, 0, 0.5)"
      justifyContent="center"
      alignItems="center"
      zIndex={9999}
    >
      <Flex
        direction="column"
        align="center"
        width="430px"
        backgroundColor="rgba(48, 49, 60, 1)"
        borderRadius="10px"
        overflow={"hidden"}
      >
        <Text color="white" fontSize="16px" margin={"32px 25px 32px 25px"}>
          {`【密码验证后】将【${actionType}】，请确保您的数据已保存`}
        </Text>
        {shouldShowPasswordInput && (
          <InputGroup size="md" flex={1} height="100%" width={"355px"}>
            <Input
              type={showViewIcon ? "text" : "password"}
              background="rgb(37, 38, 46)"
              color="white"
              variant="unstyled"
              paddingX="12px"
              paddingY="8px"
              _placeholder={{ color: "#999999;" }}
              fontSize="15px"
              borderRadius="8px"
              placeholder="输入云电脑登录密码"
              disabled={isButtonDisabled}
              onChange={(e) => {
                pwd.current = e.target.value.trim()
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !isButtonDisabled) {
                  handleConfirmPwd()
                }
              }}
            />
            <InputRightElement>
              <IconButton
                variant="text"
                aria-label={showViewIcon ? "Mask password" : "Reveal password"}
                icon={
                  showViewIcon ? (
                    <ViewOffIcon color="blue.500" />
                  ) : (
                    <ViewIcon color="blue.500" />
                  )
                }
                disabled={isButtonDisabled}
                onClick={() => setShowViewIcon(!showViewIcon)}
              />
            </InputRightElement>
          </InputGroup>
        )}
        <Flex
          align="center"
          justify="center"
          gap="6px"
          color={
            status === LoginStatus.error ? "red" : "rgba(255, 255, 255, 0.85)"
          }
          fontSize="15px"
          marginTop="8px"
          height={"20px"}
        >
          {isProcessing && isProgressMsg && (
            <Spinner boxSize="16px" thickness="2px" speed="0.9s" />
          )}
          {isSuccessMsg && (
            <Text as="span" fontSize="16px" lineHeight="1">
              ✅
            </Text>
          )}
          {displayMsg}
        </Flex>
        <Flex
          width="100%"
          height="46px"
          borderTop="1px solid rgba(255, 255, 255, 0.1)"
          marginTop={"20px"}
        >
          <Button
            flex={1}
            variant="ghost"
            height="100%"
            borderRadius={0}
            color="rgba(255, 255, 255, 0.7)"
            colorScheme="whiteAlpha"
            disabled={isButtonDisabled}
            onClick={() => {
              if (isButtonDisabled) {
                return
              }
              if (isCompleted) {
                closeDialog()
                return
              }
              handleConfirmPwd()
            }}
          >
            {confirmButtonText}
          </Button>
          <Box
            background="rgba(255, 255, 255, 0.1)"
            height="100%"
            width="1px"
          />
          <Button
            flex={1}
            height="100%"
            variant="ghost"
            borderRadius={0}
            color="rgba(255, 255, 255, 0.7)"
            colorScheme="whiteAlpha"
            disabled={isButtonDisabled}
            onClick={() => {
              if (!isButtonDisabled) {
                closeDialog()
              }
            }}
          >
            取消
          </Button>
        </Flex>
      </Flex>
    </Flex>
  )
}
export default StopResumePwdVerify
